import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Spinner } from 'reactstrap';
import {
  BackTo,
  Block,
  BlockContent,
  BlockDes,
  BlockHead,
  BlockTitle,
  Button,
  Icon,
  PreviewCard
} from '../../components/Component';
import AuthFooter from './AuthFooter';
import Head from '../../layout/head/Head';
import LoadingPageLoader from '../loaders/LoadingPageLoader';
import RedirectingPageLoader from '../loaders/RedirectingPageLoader';
import TextInput from '../components/inputs/TextInput';
import * as app_consts from "../../utils/app_consts";
import { VERIFY_TENANT_USER_ACCOUNT_OTP_API } from '../../api/api_data';
import {
  fail_verify_tenant_user_account_otp,
  loading_verify_tenant_user_account_otp,
  reset_verify_tenant_user_account_otp,
  success_verify_tenant_user_account_otp
} from '../../reducers/no_auth_tenant/verify_tenant_user_account_otp_Slice';
import {
  VERIFY_TENANT_USER_ACCOUNT_OTP_ERROR_MSG,
  VERIFY_TENANT_USER_ACCOUNT_OTP_SUCCESS_MSG
} from '../../utils/error_success_messages';
import { messageToast } from '../../utils/Utils';
import { tenant_no_auth_actions_helper } from '../../actions/actions_helper';

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      when: "beforeChildren"
    }
  },
  exit: { opacity: 0 }
};

const itemVariants = {
  hidden: { y: 30, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 10,
      mass: 0.5
    }
  }
};

const buttonVariants = {
  hover: {
    scale: 1.05,
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    transition: { duration: 0.2 }
  },
  tap: {
    scale: 0.98,
    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)"
  }
};

const inputVariants = {
  focus: {
    scale: 1.02,
    boxShadow: "0 0 0 2px rgba(59, 130, 246, 0.5)",
    transition: { duration: 0.2 }
  }
};

const initialState = { account_id: "", otp: "" };

const VerifyTenantOTP = ({ tenant_info }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [error, setError] = useState("");
  const [formData, setFormData] = useState(initialState);
  const [pageData, setPageData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const tenant_information = useSelector((state) => state.set_masked_credentials.set_masked_credentials);
  const verify_otp_listener = useSelector((state) => state.verify_tenant_user_account_otp.verify_tenant_user_account_otp);

  // Initialize page data
  useEffect(() => {
    if (tenant_information?.status === app_consts.SUCCESS && tenant_information.data)
    {
      setPageData(tenant_information.data);
      setFormData(prev => ({ ...prev, account_id: tenant_information.data?.account_id }));
    } else if (tenant_information?.status !== app_consts.LOADING)
    {
      navigate(`/${tenant_info?.url_prefix}/auth`);
    }
  }, [tenant_information, navigate, tenant_info]);

  // Handle OTP verification response
  useEffect(() => {
    if (verify_otp_listener.status === app_consts.IDLE) return;

    setIsSubmitting(verify_otp_listener.status === app_consts.LOADING);

    if (verify_otp_listener.status === app_consts.FAIL)
    {
      setError(verify_otp_listener.message);
      messageToast(verify_otp_listener.message, app_consts.FAIL);
    } else if (verify_otp_listener.status === app_consts.SUCCESS)
    {
      window.location.assign(`/${tenant_info.url_prefix}`);
    }

    if (verify_otp_listener.status !== app_consts.LOADING)
    {
      dispatch(reset_verify_tenant_user_account_otp());
    }
  }, [verify_otp_listener, dispatch, tenant_info]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.otp)
    {
      setError("Please enter the OTP code");
      return;
    }

    setError("");
    dispatch(tenant_no_auth_actions_helper(
      loading_verify_tenant_user_account_otp,
      success_verify_tenant_user_account_otp,
      fail_verify_tenant_user_account_otp,
      VERIFY_TENANT_USER_ACCOUNT_OTP_ERROR_MSG,
      VERIFY_TENANT_USER_ACCOUNT_OTP_SUCCESS_MSG,
      app_consts.POST,
      `/${tenant_info?.url_prefix}/${VERIFY_TENANT_USER_ACCOUNT_OTP_API}`,
      formData
    ));
  };

  if (!pageData)
  {
    return tenant_information?.status === app_consts.LOADING ? (
      <LoadingPageLoader tenant_info={tenant_info} />
    ) : (
      <RedirectingPageLoader tenant_info={tenant_info} />
    );
  }

  return (
    <>
      <Head title={tenant_info?.name ? `${tenant_info.name} - Verify OTP` : 'Verify OTP'} />

      <Block className="nk-block-middle nk-auth-body wide-xs">
        <AnimatePresence mode="wait">
          <motion.div
            key="content"
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={containerVariants}
          >
            {/* Brand Logo */}
            <motion.div
              variants={itemVariants}
              className="brand-logo pb-4 text-center"
            >
              <Link
                to={`/${tenant_info?.url_prefix}/auth`}
                className="logo-link"
              >
                {tenant_info?.tenant_logo && (
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    transition={{ type: "spring", stiffness: 400 }}
                  >
                    <img
                      className="logo-img logo-img-lg"
                      src={tenant_info.tenant_logo}
                      alt={`${tenant_info.name} logo`}
                      style={{ maxHeight: '80px' }}
                    />
                  </motion.div>
                )}
              </Link>
            </motion.div>

            {/* Main Card */}
            <motion.div variants={itemVariants}>
              <PreviewCard
                className="card-bordered shadow-sm"
                bodyClass="card-inner-lg"
                whileHover={{
                  scale: 1.005,
                  boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
                }}
              >
                <BlockHead>
                  <BlockContent>
                    {/* Back Button */}
                    <motion.div variants={itemVariants}>
                      <BackTo
                        link={`/${tenant_info?.url_prefix}/auth`}
                        icon="arrow-left"
                        className="text-soft"
                      >
                        Back to login
                      </BackTo>
                    </motion.div>

                    {/* Title */}
                    <motion.div variants={itemVariants}>
                      <BlockTitle
                        tag="h3"
                        className="text-center mb-2"
                        style={{
                          color: '#2c3e50',
                          fontWeight: '700',
                          fontSize: '1.5rem'
                        }}
                      >
                        {tenant_info?.name}
                      </BlockTitle>
                    </motion.div>

                    <motion.div variants={itemVariants}>
                      <BlockTitle
                        tag="h4"
                        className="text-center mb-4"
                        style={{
                          color: '#4a5568',
                          fontWeight: '500',
                          fontSize: '1.1rem'
                        }}
                      >
                        Employee Sign In
                      </BlockTitle>
                    </motion.div>

                    {/* Subtitle */}
                    <motion.div variants={itemVariants}>
                      <BlockTitle tag="h5" className="mb-2">
                        Verify OTP{' '}
                        {pageData?.first_name && (
                          <span className="text-primary">
                            for {pageData.first_name} {pageData.last_name}
                          </span>
                        )}
                      </BlockTitle>
                    </motion.div>

                    {/* Description */}
                    <motion.div variants={itemVariants}>
                      <BlockDes>
                        <p className="text-soft">
                          Enter the verification code sent to your registered email or mobile number
                        </p>
                      </BlockDes>
                    </motion.div>
                  </BlockContent>
                </BlockHead>

                {/* Error Alert */}
                <AnimatePresence>
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="mb-4"
                    >
                      <Alert color="danger" className="alert-icon">
                        <Icon name="alert-circle" />
                        <span className="ml-2">{error}</span>
                      </Alert>
                    </motion.div>
                  )}
                </AnimatePresence>

                <form onSubmit={handleSubmit}>
                  {/* OTP Input */}
                  <motion.div
                    variants={itemVariants}
                    className="form-group"
                  >
                    <TextInput
                      label="Verification Code"
                      colSize="12"
                      name="otp"
                      type="password"
                      placeholder="Enter 6-digit code"
                      value={formData.otp}
                      handleChange={handleChange}
                      maxLength="6"
                      variants={inputVariants}
                      whileFocus="focus"
                    />
                  </motion.div>

                  {/* Submit Button */}
                  <motion.div
                    variants={itemVariants}
                    className="form-group mt-4 pt-2"
                  >
                    <Button
                      as={motion.button}
                      variants={buttonVariants}
                      whileHover={!isSubmitting ? "hover" : {}}
                      whileTap={!isSubmitting ? "tap" : {}}
                      size="lg"
                      className="btn-block"
                      type="submit"
                      color="primary"
                      disabled={isSubmitting || !formData.otp}
                      style={{
                        borderRadius: '8px',
                        padding: '12px',
                        fontSize: '1rem',
                        fontWeight: '500'
                      }}
                    >
                      {isSubmitting ? (
                        <>
                          <Spinner size="sm" color="light" className="mr-2" />
                          Verifying...
                        </>
                      ) : 'Verify Code'}
                    </Button>
                  </motion.div>
                </form>
              </PreviewCard>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </Block>

      <AuthFooter tenant_name={tenant_info?.name} />
    </>
  );
};

export default VerifyTenantOTP;